/** Contrato de lugares de referencia: tiles en /app; GET por caja solo en share anónimo. */
import {
  REFERENCE_PLACE_DEFAULT_KIND,
  REFERENCE_PLACE_ICON_KINDS,
  getReferencePlaceKindColor,
  referencePlaceIconImageId,
} from './referencePlaceIcons';
import { referencePlaceKindLabel } from '../data/referencePlaceKinds';
import {
  BASE_LABEL_LAYERS_TOP_ORDER,
  moveForegroundLayersToTop,
  PROVINCE_LAYERS_TOP_ORDER,
} from '../mapLayerOrder';
import { CAD_LIST_PINS_LAYER_ID } from '../styles/mapTheme';

export const REFERENCE_PLACES_MAX_BBOX_SPAN_DEG = 2;

export const REFERENCE_PLACES_DEFAULT_LIMIT = 2000;

export const REFERENCE_PLACES_SOURCE_ID = 'reference-places';
export const REFERENCE_PLACES_SOURCE_LAYER = 'reference_places';
export const REFERENCE_PLACES_LAYER_DOTS_ID = 'reference-places-dots';
export const REFERENCE_PLACES_LAYER_CIRCLES_ID = 'reference-places-circles';
export const REFERENCE_PLACES_LAYER_IDS = [
  REFERENCE_PLACES_LAYER_CIRCLES_ID,
  REFERENCE_PLACES_LAYER_DOTS_ID,
];

export const REFERENCE_PLACES_PMTILES_S3_KEY = 'tiles/reference_places.pmtiles';
export const REFERENCE_PLACES_PMTILES_URL =
  `pmtiles://https://s3.us-east-1.amazonaws.com/argenlot.lots/${REFERENCE_PLACES_PMTILES_S3_KEY}`;

export const REFERENCE_PLACES_MIN_ZOOM = 4;
export const REFERENCE_PLACES_MAX_ZOOM = 22;

export const REFERENCE_PLACES_MOVE_DEBOUNCE_MS = 280;

const EMPTY_FEATURE_COLLECTION = { type: 'FeatureCollection', features: [] };

/**
 * @param {number} minLon
 * @param {number} minLat
 * @param {number} maxLon
 * @param {number} maxLat
 * @returns {{ minLon: number, minLat: number, maxLon: number, maxLat: number }|null}
 */
export function buildReferencePlacesBbox(minLon, minLat, maxLon, maxLat) {
  const west = Number(minLon);
  const south = Number(minLat);
  const east = Number(maxLon);
  const north = Number(maxLat);
  if (![west, south, east, north].every(Number.isFinite)) return null;
  if (east <= west || north <= south) return null;
  return { minLon: west, minLat: south, maxLon: east, maxLat: north };
}

/**
 * Caja del viewport MapLibre (`LngLatBounds`).
 *
 * @param {{ getWest?: Function, getSouth?: Function, getEast?: Function, getNorth?: Function }|null} bounds
 * @returns {{ minLon: number, minLat: number, maxLon: number, maxLat: number }|null}
 */
export function bboxFromLngLatBounds(bounds) {
  if (!bounds || typeof bounds.getWest !== 'function') return null;
  return buildReferencePlacesBbox(
    bounds.getWest(),
    bounds.getSouth(),
    bounds.getEast(),
    bounds.getNorth(),
  );
}

/**
 * @param {{ minLon: number, minLat: number, maxLon: number, maxLat: number }|null} bbox
 * @param {number} [maxSpan]
 * @returns {boolean}
 */
export function bboxExceedsMaxSpan(bbox, maxSpan = REFERENCE_PLACES_MAX_BBOX_SPAN_DEG) {
  if (!bbox) return true;
  const width = bbox.maxLon - bbox.minLon;
  const height = bbox.maxLat - bbox.minLat;
  return width > maxSpan || height > maxSpan;
}

/**
 * `minLon,minLat,maxLon,maxLat` para el query string.
 *
 * @param {{ minLon: number, minLat: number, maxLon: number, maxLat: number }|null} bbox
 * @returns {string}
 */
export function formatReferencePlacesBboxParam(bbox) {
  if (!bbox) return '';
  return `${bbox.minLon},${bbox.minLat},${bbox.maxLon},${bbox.maxLat}`;
}

/**
 * CSV de kinds / subtypes. Omite vacíos.
 *
 * @param {string[]|string|null} values
 * @returns {string}
 */
export function toReferencePlacesCsv(values) {
  const list = Array.isArray(values) ? values : [values];
  return list
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(',');
}

/**
 * Filtro MapLibre por `kind` del tile. Sin kinds no pinta nada.
 *
 * @param {string[]} [kinds]
 * @returns {Array|false}
 */
export function buildReferencePlacesKindFilter(kinds) {
  const list = [...new Set(
    (Array.isArray(kinds) ? kinds : [])
      .map((kind) => String(kind || '').trim())
      .filter(Boolean),
  )];
  if (!list.length) return false;
  if (list.length === 1) return ['==', ['get', 'kind'], list[0]];
  return ['in', ['get', 'kind'], ['literal', list]];
}

/**
 * `icon-image` desde `kind` del tile (no hay property `icon`).
 *
 * @returns {Array}
 */
export function buildReferencePlaceIconImageExpression() {
  const expression = ['match', ['to-string', ['coalesce', ['get', 'kind'], '']]];
  REFERENCE_PLACE_ICON_KINDS.forEach((kind) => {
    expression.push(kind, referencePlaceIconImageId(kind));
  });
  expression.push(referencePlaceIconImageId(REFERENCE_PLACE_DEFAULT_KIND));
  return expression;
}

/**
 * Insertar pines debajo de labels / provincias, encima de parcelas.
 *
 * @param {object} map
 * @returns {string|undefined}
 */
export function referencePlacesInsertBeforeLayerId(map) {
  if (!map || typeof map.getLayer !== 'function') return undefined;
  const candidates = [...PROVINCE_LAYERS_TOP_ORDER, ...BASE_LABEL_LAYERS_TOP_ORDER];
  return candidates.find((layerId) => map.getLayer(layerId)) || undefined;
}

/**
 * Las teselas de parcelas se agregan al tope; los pines tienen que volver
 * a quedar encima. Click/hover de lote no cambian: consultan sus capas.
 * El pin de lista CAD va al tope absoluto (por encima de toponimia): es el
 * chrome del índice y si queda bajo fills/labels se lee mal.
 *
 * @param {object} map
 */
export function moveReferencePlacesForeground(map) {
  moveForegroundLayersToTop(map, REFERENCE_PLACES_LAYER_IDS);
  if (!map || typeof map.getLayer !== 'function') return;
  try {
    if (map.getLayer(CAD_LIST_PINS_LAYER_ID)) map.moveLayer(CAD_LIST_PINS_LAYER_ID);
  } catch (_) { /* estilo a medias */ }
}

/**
 * El fallback GeoJSON y los tiles comparten source id. `setData` solo existe
 * en GeoJSON: así cada hook no borra la capa del otro.
 *
 * @param {object|null} map
 * @returns {boolean}
 */
export function isReferencePlacesGeoJsonSource(map) {
  if (!map || typeof map.getSource !== 'function') return false;
  try {
    const source = map.getSource(REFERENCE_PLACES_SOURCE_ID);
    return typeof source?.setData === 'function';
  } catch {
    return false;
  }
}

/**
 * Query para `GET /getreferenceplaces`. Sin kinds no se llama: el API
 * devolvería todo lo de la caja.
 *
 * @param {object} [selection]
 * @param {string[]} [selection.kinds]
 * @param {string[]} [selection.subtypes]
 * @param {number} [selection.limit]
 * @returns {{ bbox?: string, kinds?: string, subtypes?: string, limit?: number }}
 */
export function buildReferencePlacesQuery({ kinds, subtypes, limit } = {}) {
  const kindsCsv = toReferencePlacesCsv(kinds);
  const subtypesCsv = toReferencePlacesCsv(subtypes);
  const parsedLimit = Number(limit);
  return {
    ...(kindsCsv ? { kinds: kindsCsv } : {}),
    ...(subtypesCsv ? { subtypes: subtypesCsv } : {}),
    ...(Number.isFinite(parsedLimit) && parsedLimit > 0
      ? { limit: Math.trunc(parsedLimit) }
      : {}),
  };
}

/**
 * Kinds planos para el switcher. Conserva subtypes por si el usuario elige uno.
 *
 * @param {object} payload
 * @returns {Array<{ category: string, kind: string, label: string, subtypes: Array<{ subtype: string, label: string }> }>}
 */
export function normalizeReferencePlaceCatalog(payload) {
  const categories = payload?.data?.categories;
  if (!Array.isArray(categories)) return [];

  return categories.flatMap((category) => {
    const kinds = Array.isArray(category?.kinds) ? category.kinds : [];
    return kinds.map((entry) => {
      const kind = String(entry?.kind || '').trim();
      if (!kind) return null;
      const subtypes = Array.isArray(entry?.subtypes)
        ? entry.subtypes
          .map((subtype) => ({
            subtype: String(subtype?.subtype || '').trim(),
            label: String(subtype?.label || subtype?.subtype || '').trim(),
          }))
          .filter((subtype) => subtype.subtype)
        : [];
      return {
        category: String(category?.category || '').trim(),
        kind,
        label: String(entry?.label || referencePlaceKindLabel(kind) || kind).trim() || kind,
        subtypes,
      };
    }).filter(Boolean);
  });
}

/**
 * @param {Array<{ kind: string, label: string }>} kinds
 * @returns {Record<string, string>}
 */
export function referencePlaceKindLabels(kinds) {
  const labels = {};
  (kinds || []).forEach((entry) => {
    if (!entry?.kind) return;
    labels[entry.kind] = entry.label || referencePlaceKindLabel(entry.kind) || entry.kind;
  });
  return labels;
}

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * El GET puede mandar `lng`/`lat`, `lon`, `longitude` o un Point GeoJSON.
 *
 * @param {object|null} place
 * @returns {{ lng: number, lat: number }|null}
 */
export function readPlaceLngLat(place) {
  if (!place || typeof place !== 'object') return null;
  const lng = toFiniteNumber(
    place.lng ?? place.lon ?? place.longitude ?? place.x,
  );
  const lat = toFiniteNumber(
    place.lat ?? place.latitude ?? place.y,
  );
  if (lng != null && lat != null) return { lng, lat };

  const geometry = place.geometry || place.geom || place.location;
  const coords = Array.isArray(geometry?.coordinates)
    ? geometry.coordinates
    : (Array.isArray(place.coordinates) ? place.coordinates : null);
  if (coords?.length >= 2) {
    const fromGeomLng = toFiniteNumber(coords[0]);
    const fromGeomLat = toFiniteNumber(coords[1]);
    if (fromGeomLng != null && fromGeomLat != null) {
      return { lng: fromGeomLng, lat: fromGeomLat };
    }
  }
  return null;
}

/**
 * GeoJSON de pines. Sin `properties` crudas del ingest.
 *
 * @param {Array<object>} places
 * @param {Record<string, string>} [kindLabels]
 * @returns {GeoJSON.FeatureCollection}
 */
export function placesToGeoJSON(places, kindLabels = {}) {
  const features = (Array.isArray(places) ? places : [])
    .map((place) => {
      const props = place?.properties && typeof place.properties === 'object'
        ? place.properties
        : {};
      const coords = readPlaceLngLat(place);
      if (!coords) return null;
      const kind = String(place?.kind || props.kind || '').trim();
      const subtype = String(place?.subtype || props.subtype || '').trim();
      const name = String(place?.name || props.name || '').trim();
      return {
        type: 'Feature',
        id: place?.id ?? props.id,
        geometry: { type: 'Point', coordinates: [coords.lng, coords.lat] },
        properties: {
          id: place?.id,
          name,
          kind,
          subtype,
          category: String(place?.category || '').trim(),
          kindLabel: kindLabels[kind] || referencePlaceKindLabel(kind) || kind,
          icon: referencePlaceIconImageId(kind),
          color: getReferencePlaceKindColor(kind),
        },
      };
    })
    .filter(Boolean);

  return { type: 'FeatureCollection', features };
}

export function emptyReferencePlacesGeoJSON() {
  return EMPTY_FEATURE_COLLECTION;
}

/**
 * Feature de lugar bajo el cursor, si la capa está montada.
 *
 * @param {object|null} map
 * @param {{ x: number, y: number }|null} point
 * @returns {object|null}
 */
export function queryReferencePlaceAtPoint(map, point) {
  if (!map || !point || typeof map.getLayer !== 'function') return null;
  const layers = REFERENCE_PLACES_LAYER_IDS.filter((layerId) => {
    try { return Boolean(map.getLayer(layerId)); } catch { return false; }
  });
  if (!layers.length) return null;
  try {
    const hits = map.queryRenderedFeatures(point, { layers });
    return hits?.[0] || null;
  } catch {
    return null;
  }
}

/**
 * @param {object} response Envelope de `getReferencePlaces`.
 * @returns {{ places: object[], returned: number, limit: number, truncated: boolean }}
 */
export function readReferencePlacesResponse(response) {
  const data = response?.data || {};
  const places = Array.isArray(data.places)
    ? data.places
    : (Array.isArray(data.features) ? data.features : []);
  const returned = Number.isFinite(Number(data.returned))
    ? Number(data.returned)
    : places.length;
  const limit = Number.isFinite(Number(data.limit))
    ? Number(data.limit)
    : REFERENCE_PLACES_DEFAULT_LIMIT;
  return {
    places,
    returned,
    limit,
    truncated: returned >= limit && limit > 0,
  };
}
